import {Component, DestroyRef, effect, inject, Injector, OnInit, signal, input, output, ChangeDetectionStrategy} from "@angular/core"
import {takeUntilDestroyed} from "@angular/core/rxjs-interop"
import {MatTableModule} from "@angular/material/table"
import {MatTooltipModule} from "@angular/material/tooltip"
import {AssociatedJobsTableItemFragment} from "@app/platform/components/jobs/associated-jobs-table/associated-jobs-table.generated"
import {IsDefined} from "@cm/utils/filter"
import {OverflowableTextComponent} from "@common/components/data"
import {EntityMetadataComponent} from "@common/components/entity/entity-metadata/entity-metadata.component"
import {FullPageFeedbackComponent} from "@common/components/full-page-feedback/full-page-feedback.component"
import {TableCellComponent} from "@common/components/tables/table-cell/table-cell.component"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {keepCheckingRunningJobs$} from "@common/helpers/jobs"
import {AuthService} from "@common/services/auth/auth.service"
import {RefreshService} from "@common/services/refresh/refresh.service"
import {TabStateService} from "@common/services/tab-state/tab-state.service"
import {ContentTypeModel, JobState} from "@generated"
import {AssociatedJobsTableItemsGQL} from "@platform/components/jobs/associated-jobs-table/associated-jobs-table.generated"
import {JobStateTooltipComponent} from "@platform/components/jobs/job-state-tooltip/job-state-tooltip.component"
import {JobThumbnailComponent} from "@platform/components/jobs/job-thumbnail/job-thumbnail.component"
import {LoadedData} from "@platform/models/data"
import {PLACEHOLDER_ITEMS} from "@platform/models/data/constants"
import {DataLoaderService} from "@platform/services/data"
import {startWith, switchMap} from "rxjs"
import {v4 as uuid4} from "uuid"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-associated-jobs-table",
    imports: [
        FullPageFeedbackComponent,
        MatTableModule,
        TableCellComponent,
        OverflowableTextComponent,
        MatTooltipModule,
        JobThumbnailComponent,
        EntityMetadataComponent,
    ],
    templateUrl: "./associated-jobs-table.component.html",
    styleUrl: "./associated-jobs-table.component.scss",
})
export class AssociatedJobsTableComponent implements OnInit {
    readonly $displayedColumns = input(["id", "state", "name", "creator"], {alias: "displayedColumns"})
    readonly clickItem = output<AssociatedJobsTableItemFragment | undefined>()

    readonly auth = inject(AuthService)
    readonly refresh = inject(RefreshService)

    // Fetch mode: query the jobs assigned to this entity.
    readonly $assignedTo = input<{
        id?: string
        legacyId?: number
        contentTypeModel: ContentTypeModel
    }>(undefined, {alias: "assignedTo"})

    // Provided mode: render a caller-supplied list of jobs instead of fetching by assignment.
    // `undefined` means "still loading"; an (empty) array means "loaded". Mutually exclusive with assignedTo.
    readonly $jobs = input<AssociatedJobsTableItemFragment[] | undefined>(undefined, {alias: "jobs"})

    readonly dataLoader = inject(DataLoaderService)
    readonly destroyRef = inject(DestroyRef)
    readonly tabState = inject(TabStateService)
    readonly injector = inject(Injector)

    readonly $data = signal(PLACEHOLDER_ITEMS(5) as LoadedData<AssociatedJobsTableItemFragment>)

    private readonly associatedJobsTableItems = inject(AssociatedJobsTableItemsGQL)

    constructor() {
        // Provided mode: mirror the caller-supplied jobs into the table once they have loaded.
        effect(() => {
            const providedJobs = this.$jobs()
            if (providedJobs !== undefined) {
                this.$data.set({
                    items: providedJobs.map((job) => ({trackId: uuid4(), data: job, error: null})),
                    error: null,
                    complete: true,
                    totalCount: providedJobs.length,
                })
            }
        })
    }

    ngOnInit() {
        const assignedTo = this.$assignedTo()
        // Provided mode (jobs passed in): the effect mirrors them into the table; nothing to fetch.
        if (!assignedTo) {
            return
        }

        this.tabState.becomesActive$
            .pipe(
                switchMap(() =>
                    fetchThrowingErrors(this.associatedJobsTableItems)({
                        objectId: assignedTo.id,
                        objectLegacyId: assignedTo.legacyId,
                        contentTypeModel: assignedTo.contentTypeModel,
                    }).then((data) => ({
                        items: data.items.filter(IsDefined).map((item) => ({trackId: uuid4(), data: item?.job, error: null})),
                        totalCount: data.totalCount,
                        error: null,
                        complete: true,
                    })),
                ),
                startWith(PLACEHOLDER_ITEMS(5) as LoadedData<AssociatedJobsTableItemFragment>),
            )
            .pipe(takeUntilDestroyed(this.destroyRef), keepCheckingRunningJobs$(this.injector, this.tabState))
            .subscribe((data) => {
                this.$data.set({
                    items: data.items,
                    error: null,
                    complete: false,
                    totalCount: data.totalCount,
                })
            })
    }

    protected readonly JobState = JobState
    protected readonly JobStateTooltipComponent = JobStateTooltipComponent
}
